import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useLangNavigate from "../../hooks/useLangNavigate";
import { useAuth } from "../../hooks/useAuth";
import OffoodLogo from "../brand/OffoodLogo";
import Button from "../buttons/Button";
import LangButton from "../buttons/LangButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightToBracket,
  faGridHorizontal,
} from "@fortawesome/pro-light-svg-icons";

/**
 * Shared header component for all landing pages
 * Matches the design from LandingEntry.js
 * Transparent at top, solid background on scroll
 */
function LandingHeader() {
  const { t } = useTranslation();
  const navigate = useLangNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Trigger background change after scrolling 50px
      setIsScrolled(window.scrollY > 50);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Navigation links for sub-landings
  const navLinks = [
    { path: "restaurants", label: t("meta.landingRestaurants.title") },
    { path: "consumers", label: t("meta.landingConsumers.title") },
    { path: "companies", label: t("meta.landingCompanies.title") },
    { path: "about", label: t("meta.landingAbout") },
  ];

  // Check if current path matches the nav link
  const isActive = (path) => location.pathname.includes(path);

  return (
    <header
      className={`landing-header landing-header--dark landing-header--sticky${isScrolled ? " landing-header--scrolled" : ""}`}
    >
      <div className="landing-header__container">
        <div
          className="landing-header__logo"
          onClick={() => navigate("/")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && navigate("/")}
        >
          <OffoodLogo width="120px" variant="red" />
        </div>
        <nav className="landing-header__nav">
          {/* Sub-landing navigation links */}
          <div className="landing-header__nav-links">
            {navLinks.map((link) => (
              <button
                key={link.path}
                className={`landing-header__nav-link${isActive(link.path) ? " landing-header__nav-link--active" : ""}`}
                onClick={() => navigate(link.path)}
              >
                {link.label}
              </button>
            ))}
          </div>
          <LangButton size="sm" flagOnly />
          {isAuthenticated ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate("dashboard")}
            >
              <FontAwesomeIcon icon={faGridHorizontal} className="mr-2" />
              {t("landing.header.dashboard")}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate("login")}
            >
              <FontAwesomeIcon icon={faArrowRightToBracket} className="mr-2" />
              {t("landing.header.login")}
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
}

export default LandingHeader;
